import { motion } from "framer-motion";
import { MessageCircle, Phone, Sparkles, Users, Crown, Building2 } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

const WA_URL = (plan) => `https://wa.me/919819933676?text=${encodeURIComponent(`Namaste! Mujhe Divya Dhara ka ${plan} plan lena hai. Please contact karein. 🥛`)}`;

const PLANS = [
  {
    slug: "starter",
    icon: Sparkles,
    name: "Starter",
    qty: "0.5 L / day",
    price: "₹50",
    price_unit: "per day",
    nameHi: "स्टार्टर",
    qtyHi: "0.5 लीटर / दिन",
    note: "Perfect for small households",
    noteHi: "छोटे परिवारों के लिए",
    highlighted: false,
    corporate: false,
  },
  {
    slug: "family",
    icon: Users,
    name: "Family",
    qty: "1 L / day",
    price: "₹100",
    price_unit: "per day",
    nameHi: "फैमिली",
    qtyHi: "1 लीटर / दिन",
    note: "Most popular — 2,000+ families",
    noteHi: "सबसे लोकप्रिय — 2,000+ परिवार",
    highlighted: true,
    corporate: false,
  },
  {
    slug: "premium",
    icon: Crown,
    name: "Premium",
    qty: "2 L / day",
    price: "₹200",
    price_unit: "per day",
    nameHi: "प्रीमियम",
    qtyHi: "2 लीटर / दिन",
    note: "For growing families & gifting",
    noteHi: "बड़े परिवारों व उपहार हेतु",
    highlighted: false,
    corporate: false,
  },
  {
    slug: "corporate",
    icon: Building2,
    name: "Corporate",
    qty: "5 L+ / day",
    price: "₹500",
    price_unit: "onwards",
    nameHi: "कॉर्पोरेट",
    qtyHi: "5 लीटर+ / दिन",
    note: "Offices, events, gifting · GST invoice",
    noteHi: "कार्यालय, आयोजन, उपहार · GST बिल",
    highlighted: false,
    corporate: true,
  },
];

function PlanCard({ p, i, lang }) {
  const Icon = p.icon;
  const isDark = p.corporate;
  const isFeatured = p.highlighted;
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.65, delay: i * 0.09, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -6 }}
      className={`relative rounded-2xl p-7 md:p-8 flex flex-col transition-all border ${
        isDark
          ? "bg-gradient-to-br from-[#14351F] to-[#0A1F15] border-dd-gold shadow-[0_25px_60px_-20px_rgba(201,162,76,0.45)] text-dd-cream"
          : isFeatured
          ? "bg-white border-dd-gold shadow-[0_25px_60px_-20px_rgba(201,162,76,0.35)] ring-1 ring-dd-gold/30"
          : "bg-white border-dd-line shadow-[0_10px_40px_-18px_rgba(15,46,32,0.18)] hover:border-dd-gold/60"
      }`}
      data-testid={`plan-${p.slug}`}
    >
      {isFeatured && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-b from-dd-gold to-[#8F6E28] text-dd-ink-dark text-[0.62rem] font-bold px-4 py-1.5 rounded-full tracking-[0.22em] uppercase shadow-md">
          {lang === "hi" ? "लोकप्रिय" : "Most Popular"}
        </div>
      )}

      {/* Icon badge */}
      <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
        isDark
          ? "bg-dd-gold/15 border border-dd-gold/40"
          : "bg-gradient-to-br from-dd-gold-soft to-dd-gold-light/40"
      }`}>
        <Icon className={`w-6 h-6 ${isDark ? "text-dd-gold" : "text-[#8F6E28]"}`} strokeWidth={1.5} />
      </div>

      {/* Name */}
      <div className={`font-serif text-3xl mt-5 tracking-tight ${isDark ? "text-dd-cream" : "text-dd-ink"}`}>
        {lang === "hi" ? p.nameHi : p.name}
      </div>
      <div className={`text-[0.68rem] tracking-[0.25em] uppercase mt-1.5 ${isDark ? "text-dd-gold" : "text-[#8F6E28]"}`}>
        {lang === "hi" ? p.qtyHi : p.qty}
      </div>

      {/* Divider */}
      <div className={`h-px w-full my-6 ${isDark ? "bg-dd-gold/30" : "bg-dd-line"}`} />

      {/* Price */}
      <div className="flex items-baseline gap-2">
        <span className={`font-serif text-5xl md:text-[3.25rem] font-semibold tracking-tight ${isDark ? "text-dd-gold" : "text-dd-ink"}`}>
          {p.price}
        </span>
        <span className={`text-xs tracking-wide ${isDark ? "text-dd-cream-muted" : "text-dd-ink-muted"}`}>
          {p.price_unit}
        </span>
      </div>

      {/* Note */}
      <p className={`text-sm mt-3 leading-relaxed font-serif italic ${isDark ? "text-dd-cream-muted" : "text-dd-ink-muted"}`}>
        {lang === "hi" ? p.noteHi : p.note}
      </p>

      {/* CTA */}
      <div className="mt-7 pt-5">
        {p.corporate ? (
          <a href="tel:+919819933676"
            className="w-full inline-flex items-center justify-center gap-2 bg-dd-gold hover:bg-dd-gold-light text-dd-ink-dark px-5 py-3 rounded-full font-bold text-sm tracking-wide transition-colors"
            data-testid={`plan-${p.slug}-cta`}>
            <Phone className="w-4 h-4" strokeWidth={2.25} /> {lang === "hi" ? "कॉल करें" : "Call Now"}
          </a>
        ) : (
          <a href={WA_URL(p.name)} target="_blank" rel="noreferrer"
            className={`w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-bold text-sm tracking-wide transition-colors ${
              isFeatured
                ? "bg-gradient-to-b from-dd-gold to-[#8F6E28] hover:from-dd-gold-light hover:to-dd-gold text-dd-ink-dark shadow-md"
                : "bg-[#1a3a2a] hover:bg-[#235040] text-dd-cream"
            }`}
            data-testid={`plan-${p.slug}-cta`}>
            <MessageCircle className="w-4 h-4" /> {lang === "hi" ? "WhatsApp पर बुक करें" : "Book on WhatsApp"}
          </a>
        )}
      </div>
    </motion.div>
  );
}

export default function SubscriptionPlans() {
  const { lang } = useI18n();
  return (
    <section id="plans" className="dd-section bg-dd-ivory relative overflow-hidden" data-testid="plans-section">
      <div className="absolute inset-0 opacity-20 pointer-events-none">
        <div className="absolute top-20 left-1/4 w-96 h-96 rounded-full" style={{ background: "radial-gradient(circle, #C9A24C 0%, transparent 60%)" }} />
      </div>
      <div className="dd-container relative">
        <div className="text-center mb-14 max-w-2xl mx-auto">
          <div className="dd-label text-dd-gold">{lang === "hi" ? "सदस्यता योजनाएं" : "Subscription Plans"}</div>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-dd-cream mt-3 tracking-tight">
            {lang === "hi" ? "हर घर के लिए एक योजना" : "A Plan for Every Family"}
          </h2>
          <div className="dd-ornament my-5"><span className="text-lg">✦</span></div>
          <p className="text-dd-cream-muted text-sm">
            {lang === "hi" ? "कोई लॉक-इन नहीं · कभी भी रद्द करें · प्रातः 6 बजे डिलीवरी" : "No lock-in · Cancel anytime · 6 AM doorstep delivery"}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {PLANS.map((p, i) => (
            <PlanCard key={p.slug} p={p} i={i} lang={lang} />
          ))}
        </div>

        <p className="text-center text-xs text-dd-cream-muted mt-10 tracking-wide">
          {lang === "hi" ? "शुद्ध गौमाता घी — मूल्य हेतु +91 98199 33676 पर कॉल करें" : "Pure Cow Ghee — Price on request. Call +91 98199 33676"}
        </p>
      </div>
    </section>
  );
}
